import React, { useState, useContext } from 'react';
import axios from 'axios';
import { AuthContext } from '../App';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { toast } from 'sonner';
import PropertySetupForm from './PropertySetupForm';
import InspectionImportTab from './inspection-import/InspectionImportTab';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function CreateInspectionDialog({ open, onClose, onSuccess }) {
  const { token } = useContext(AuthContext);
  const [activeTab, setActiveTab] = useState('basic');
  const [basicData, setBasicData] = useState({
    property_address: '',
    valuation_amount: ''
  });
  const [propertyDetails, setPropertyDetails] = useState({
    address_line1: '',
    city: '',
    postcode: '',
    country: 'United Kingdom'
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (activeTab === 'import') return;
    setLoading(true);

    try {
      const fullAddress = propertyDetails.address_line1
        ? `${propertyDetails.address_line1}, ${propertyDetails.city}, ${propertyDetails.postcode}`
        : basicData.property_address;

      await axios.post(
        `${API}/inspections`,
        {
          property_address: fullAddress,
          valuation_amount: basicData.valuation_amount ? parseFloat(basicData.valuation_amount) : null,
          property_details: propertyDetails.address_line1 ? propertyDetails : null
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Inspection created successfully');
      setBasicData({ property_address: '', valuation_amount: '' });
      setPropertyDetails({ address_line1: '', city: '', postcode: '', country: 'United Kingdom' });
      setActiveTab('basic');
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create inspection');
    } finally {
      setLoading(false);
    }
  };

  const handleImportComplete = (inspectionId) => {
    onSuccess();
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="create-inspection-dialog">
        <DialogHeader>
          <DialogTitle data-testid="create-inspection-title">Create New Inspection</DialogTitle>
          <DialogDescription>Add property details manually or import from inspection sheets</DialogDescription>
        </DialogHeader>
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="basic" data-testid="basic-tab">Basic Info</TabsTrigger>
            <TabsTrigger value="property" data-testid="property-tab">Property Setup</TabsTrigger>
            <TabsTrigger value="import" data-testid="import-tab">Import from Sheet / Photos</TabsTrigger>
          </TabsList>

          <form onSubmit={handleSubmit}>
            <TabsContent value="basic" className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label htmlFor="property-address">Property Address (Quick Entry)</Label>
                <Input
                  id="property-address"
                  placeholder="123 Main Street, City, Postcode"
                  value={basicData.property_address}
                  onChange={(e) => setBasicData({ ...basicData, property_address: e.target.value })}
                  data-testid="property-address-input"
                />
                <p className="text-xs text-muted-foreground">
                  Or use Property Setup tab for detailed structured entry
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="valuation">Valuation Amount (£)</Label>
                <Input
                  id="valuation"
                  type="number"
                  placeholder="250000"
                  value={basicData.valuation_amount}
                  onChange={(e) => setBasicData({ ...basicData, valuation_amount: e.target.value })}
                  data-testid="valuation-amount-input"
                />
              </div>
              <DialogFooter className="mt-6">
                <Button type="button" variant="outline" onClick={onClose} data-testid="cancel-create-button">
                  Cancel
                </Button>
                <Button type="submit" disabled={loading} data-testid="submit-create-button">
                  {loading ? 'Creating...' : 'Create Inspection'}
                </Button>
              </DialogFooter>
            </TabsContent>

            <TabsContent value="property" className="mt-4">
              <PropertySetupForm
                propertyDetails={propertyDetails}
                onChange={setPropertyDetails}
              />
              <DialogFooter className="mt-6">
                <Button type="button" variant="outline" onClick={onClose} data-testid="cancel-create-button-prop">
                  Cancel
                </Button>
                <Button type="submit" disabled={loading} data-testid="submit-create-button-prop">
                  {loading ? 'Creating...' : 'Create Inspection'}
                </Button>
              </DialogFooter>
            </TabsContent>
          </form>

          <TabsContent value="import" className="mt-4">
            <InspectionImportTab
              onInspectionCreated={handleImportComplete}
              onCancel={onClose}
            />
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
